import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, unref as _unref, createBlock as _createBlock, createVNode as _createVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-23770082"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "flex align-items-center justify-content-between mt-5 mb-2" }
const _hoisted_2 = { class: "font-bold" }
const _hoisted_3 = {
  key: 0,
  class: "pi pi-exclamation-circle text-orange-500 ml-2 cursor-pointer"
}
const _hoisted_4 = { class: "mt-2 tag-list" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = {
  key: 0,
  class: "pi pi-check tag-check-icon"
}

import { ref, inject, onBeforeMount } from "vue";
import { get, set, useArrayFindIndex } from "@vueuse/core";

// PrimeVue関連のインポート
import Dialog from "primevue/dialog";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import Toast from "primevue/toast";
import { useToast } from "primevue/usetoast";
import Tag from "primevue/tag";
import Button from "primevue/button";

// 型定義のインポート
import { TagTypeEnum, TagTypeOrderEnum } from "@/clinic/typings/enums/tag";
import { ApiCustomerSaveTagsReq, ITag } from "@/clinic/typings";

// サービスのインポート
import { TagService, CustomerTagService, CustomerServices } from "@/clinic/services";

// ユーティリティ関数のインポート
import { notityErrorMsg } from "@/clinic/utils/notify-utils";

// HTTP関連のインポート
import { AxiosError, HttpStatusCode } from "axios";

/**
 * 顧客情報更新イベントを発行
 * @emits refreshCustomer - 顧客情報の再取得を要求するイベント
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'dialog',
  emits: ["refreshCustomer"],
  setup(__props, { expose, emit: emits }) {

/**
 * 顧客タグ編集ダイアログコンポーネント
 * 顧客の手動タグを選択・編集するためのダイアログ
 */

// Vue関連のインポート


/** フォームで選択されたタグ一覧 */
const formTags = ref<ITag[]>([]);

/** パートナータグ一覧 */
const partnerFormTags = ref<ITag[]>([]);

/** カテゴリ別テンプレートタグ一覧 */
const templateTags = ref<Record<string, ITag[]>>({});

/** ダイアログの表示状態 */
const visible = ref(false);

/** クリニックID */
const clinicId = ref(0);

/** 顧客ID */
const customerId = ref(0);

/** 利用可能なタグ一覧 */
const tags = ref<ITag[]>([]);

/** トースト通知のインスタンス */
const toast = useToast();

/**
 * クリニックIDパラメータを取得
 * @returns {Promise<{clinic_id: number}>} クリニックIDパラメータ
 */
const clinicIdParams = async () => ({ clinic_id: get(await get(clinicId)) });

/**
 * タグ一覧を取得
 * @param {number} tag_template_ids - タグテンプレートID（デフォルト: 0）
 */
const loadTags = async (tag_template_ids = 0) => {
  const params = {
    ...(await clinicIdParams()),
    type: TagTypeEnum.manual,
  };
  if (tag_template_ids != 0) {
    (params as any)["tag_template_ids[]"] = tag_template_ids;
  }

  const { data: tagsData } = await TagService.getTags(
    params,
    () => undefined,
    () => undefined
  );
  set(tags, get(tagsData) as unknown as ITag[]);
  if (get(tags)) {
    assignTagsByCategory();
  }
};

/**
 * タグをカテゴリ別にグループ化
 * カテゴリ名がある場合はカテゴリ別に、ない場合は「カテゴリ未選択」に分類
 * @returns {Record<string, ITag[]>} カテゴリ別にグループ化されたタグ
 */
const assignTagsByCategory = () => {
  if (!get(tags)) return;

  const results = get(tags).reduce((result: Record<string, ITag[]>, item) => {
    // カテゴリ名がある場合はカテゴリ別にグループ化
    if (item.category_name) {
      (result[item.category_name] = result[item.category_name] || []).push(item);
    } else {
      // カテゴリがない場合は「カテゴリ未選択」に分類
      (result["カテゴリー未選択"] = result["カテゴリー未選択"] || []).push(item);
    }
    return result;
  }, {});

  // カテゴリをソートして「カテゴリ未選択」を最後に表示
  const sortedResults: Record<string, ITag[]> = {};

  // カテゴリ名とposition_numberのマッピングを作成
  const categoryWithPosition: Array<{ name: string; position: number }> = [];

  Object.keys(results).forEach(categoryName => {
    if (categoryName === "カテゴリー未選択") {
      // 「カテゴリ未選択」は最後に表示（position: 9999）
      categoryWithPosition.push({ name: categoryName, position: 9999 });
    } else {
      // 設定済みカテゴリは最初のタグのposition_numberを使用
      const firstTag = results[categoryName][0];
      const position = firstTag?.position_number || 0;
      categoryWithPosition.push({ name: categoryName, position });
    }
  });

  // position_numberで昇順ソート
  categoryWithPosition.sort((a, b) => a.position - b.position);

  // ソートされた順序で結果を構築
  categoryWithPosition.forEach(({ name }) => {
    sortedResults[name] = results[name];
  });

  set(templateTags, sortedResults);
  return sortedResults;
};

/**
 * 顧客の現在のタグを取得
 * 手動タグとパートナータグを分けて取得
 */
const loadCustomerTags = async () => {
  const response = await CustomerTagService.getTags(
    { ...(await clinicIdParams()), customer_id: get(customerId), type: TagTypeEnum.manual },
    () => undefined,
    () => undefined
  );
  let tagsData: ITag[] = [];
  if (response && response.data) {
    const data = get(response.data);
    tagsData = Array.isArray(data) ? data : [];
  } else if (Array.isArray(response)) {
    tagsData = response;
  }
  formTags.value = tagsData
    .filter((tag: ITag) => tag.type != TagTypeEnum.partner)
    .sort(
      (first: ITag, second: ITag) =>
        TagTypeOrderEnum[first.type as keyof typeof TagTypeOrderEnum] -
        TagTypeOrderEnum[second.type as keyof typeof TagTypeOrderEnum]
    );
  partnerFormTags.value = tagsData.filter((tag: ITag) => tag.type == TagTypeEnum.partner);
};

/**
 * タグの選択状態を切り替え
 * @param {ITag} tag - 選択/解除するタグ
 */
const onSelectTag = (tag: ITag) => {
  const index = get(useArrayFindIndex(formTags, ({ id }: ITag) => id == tag?.id));
  if (index < 0) {
    // タグを選択状態に追加
    formTags.value.push(tag);
  } else {
    // タグを選択状態から削除
    formTags.value.splice(index, 1);
  }
};

/**
 * タグが選択されているかどうかを判定
 * @param {ITag} tag - 判定するタグ
 * @returns {boolean} 選択されている場合true
 */
const isTagSelected = (tag: ITag) => {
  return formTags.value.some(selectedTag => selectedTag.id == tag.id);
};

/**
 * ダイアログを表示
 */
const showDialog = () => {
  refreshTags();
  set(visible, true);
};

/**
 * ダイアログを非表示
 */
const hideDialog = () => set(visible, false);

/**
 * ダイアログを非表示にして選択状態をリセット
 */
const hideAndResetSelectedTabView = () => {
  hideDialog();
};

/**
 * 顧客IDを設定
 * @param {number} id - 顧客ID
 */
const setCustomerId = (id: number) => {
  set(customerId, id);
};

/**
 * タグの保存処理
 * 選択されたタグを顧客に紐付けて保存
 */
const onSaving = async () => {
  const tags = [...get(formTags), ...get(partnerFormTags)];
  const availableTags = tags.filter(tag => Boolean(tag?.id));
  const newTags = tags.filter(tag => !tag?.id);
  const payload = {
    id: get(customerId),
    type: TagTypeEnum.manual,
    clinic_id: get(await get(clinicId)),
    customers_tags_attributes:
      availableTags?.map(({ id }: ITag) => {
        return { tag_id: id };
      }) || [],
    tags_attributes: newTags || [],
  };
  await CustomerServices.saveTags(
    payload as ApiCustomerSaveTagsReq,
    () => {
      emits("refreshCustomer");
      hideDialog();
    },
    (error: AxiosError<{ errors: string[] }>) => {
      if (error?.response?.status == HttpStatusCode.UnprocessableEntity) {
        const [errMsg] = error?.response?.data?.errors || [];
        notityErrorMsg(toast, errMsg);
      }
    }
  );
};

/**
 * タグ情報を再取得
 * 顧客タグと利用可能なタグの両方を更新
 */
const refreshTags = async () => {
  await loadCustomerTags();
  await loadTags();
};

onBeforeMount(async () => {
  await set(clinicId, await inject("clinicId"));
});

/**
 * コンポーネントの外部公開メソッド
 */
// eslint-disable-next-line no-undef
expose({
  showDialog,
  setCustomerId,
  refreshTags,
});

/**
 * タグ登録ページに遷移
 * 新しいタブでセグメント用手動タグ登録ページを開く
 */
const onGoToTagRegister = () => {
  window.open("/clinic/segment_manual_tags", "_blank");
};

return (_ctx: any,_cache: any) => {
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(Dialog), {
      visible: visible.value,
      "onUpdate:visible": _cache[0] || (_cache[0] = ($event: any) => ((visible).value = $event)),
      modal: "",
      header: "タグを編集",
      draggable: false,
      class: "w-34.4rem tag-dialog border-noround",
      onHide: hideAndResetSelectedTabView
    }, {
      footer: _withCtx(() => [
        _createVNode(_unref(Button), {
          label: "キャンセル",
          class: "inline-block p-gray border-noround",
          onClick: hideAndResetSelectedTabView,
          text: ""
        }),
        _createVNode(_unref(Button), {
          label: "保存",
          class: "inline-block p-orange border-noround",
          onClick: onSaving
        })
      ]),
      default: _withCtx(() => [
        _createVNode(_unref(TabView), null, {
          default: _withCtx(() => [
            _createVNode(_unref(TabPanel), {
              header: "手動タグ",
              headerClass: "tag-manual"
            }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(Object.keys(templateTags.value), (categoryName, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    class: "mt-5 mb-2",
                    key: index
                  }, [
                    _createElementVNode("div", _hoisted_1, [
                      _createElementVNode("span", _hoisted_2, [
                        _createTextVNode(_toDisplayString(categoryName) + " ", 1),
                        (categoryName === 'カテゴリー未選択')
                          ? _withDirectives((_openBlock(), _createElementBlock("i", _hoisted_3, null, 512)), [
                              [
                                _directive_tooltip,
                                '設定 > セグメント用手動タグ登録 からカテゴリーを設定してください',
                                void 0,
                                { right: true }
                              ]
                            ])
                          : _createCommentVNode("", true)
                      ]),
                      (index === 0)
                        ? (_openBlock(), _createBlock(_unref(Button), {
                            key: 0,
                            label: "タグ登録はこちら",
                            class: "p-button-link tag-register-link",
                            icon: "pi pi-external-link",
                            iconPos: "right",
                            onClick: onGoToTagRegister
                          }))
                        : _createCommentVNode("", true)
                    ]),
                    _createElementVNode("div", _hoisted_4, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(templateTags.value[categoryName], (tag) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: tag.id,
                          class: _normalizeClass(['mr-0.25 mb-0.25 font-medium cursor-pointer relative', isTagSelected(tag) ? 'tag-selected' : '']),
                          onClick: ($event: any) => (onSelectTag(tag))
                        }, [
                          _createVNode(_unref(Tag), {
                            value: tag.name
                          }, null, 8, ["value"]),
                          (isTagSelected(tag))
                            ? (_openBlock(), _createElementBlock("i", _hoisted_6))
                            : _createCommentVNode("", true)
                        ], 10, _hoisted_5))
                      }), 128))
                    ])
                  ]))
                }), 128))
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["visible"]),
    _createVNode(_unref(Toast), { position: "top-center" })
  ], 64))
}
}

})